import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, ExternalLink, Send, Download, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { fmtMT } from "@/lib/mountainTime";

const B = "/admin/fb-page";
const err = (e) => e?.response?.data?.detail || "Something went wrong";
const LABEL = { queued: "Posting…", scheduled: "Scheduled", publishing: "Posting…", published: "Posted", failed: "Failed", uncertain: "Uncertain — check Facebook", cancelled: "Cancelled",
  test_posted: "Hidden test post created", test_deleted: "Hidden test post deleted" };

const Connection = ({ c }) => {
  if (!c) return <p className="text-xs text-stone-500">Checking the Meta connection…</p>;
  if (!c.connected) return null;
  const exp = c.never_expires ? "never expires" : c.expires_at ? `expires ${fmtMT(c.expires_at)}` : "";
  return (
    <div data-testid="fb-page-connection" className={`text-xs rounded-md border px-2 py-1 ${c.ok && !c.renew_soon ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
      {c.ok ? <>Connected to <b>{c.page_name}</b> (Page ID {c.page_id}) · token {exp}{c.data_access_expires_at ? ` · data access until ${fmtMT(c.data_access_expires_at)}` : ""}</> : c.problem}
      {c.ok && c.renew_soon && <div className="font-semibold">Renew the Page token soon: it stops working within 14 days.</div>}
    </div>
  );
};

const TestPost = ({ job, onDone }) => {
  const del = async () => { try { await api.delete(`${B}/jobs/${job.id}/test-post`); toast.success("Hidden test post deleted"); onDone(); } catch (e) { toast.error(err(e)); } };
  return (
    <div data-testid="fb-page-test-post" className="text-xs rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 space-y-1">
      <p>Hidden test post created (ID {job.fb_post_id}). It isn't visible to the public. Check it in Meta Business Suite → Content, then delete it.</p>
      <Button data-testid="fb-page-test-delete" size="sm" variant="outline" className="h-7" onClick={del}>Delete hidden test post</Button>
    </div>
  );
};

const Preview = ({ pkg, hid }) => (
  <div data-testid="fb-page-preview" className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-2">
    <div className="grid grid-cols-4 sm:grid-cols-6 gap-1">
      {pkg.photos_list.map((m) => (
        <div key={m.key} className="relative aspect-square rounded overflow-hidden bg-stone-200">
          <img src={`${API}/horses/${hid}/media-files/${m.key}/download`} alt={m.name} className="h-full w-full object-cover" loading="lazy" />
          <span className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[10px] rounded px-1">{m.order}</span>
        </div>
      ))}
    </div>
    <pre data-testid="fb-page-preview-text" className="whitespace-pre-wrap [overflow-wrap:anywhere] font-sans text-xs text-stone-800 max-h-64 overflow-auto">{pkg.ad_text}</pre>
  </div>
);

const Videos = ({ job, hid, onDone }) => {
  if (!job.videos?.length) return null;
  const mark = async (v, added) => { try { await api.post(`${B}/jobs/${job.id}/videos/${v.order}`, { added }); onDone(); } catch (e) { toast.error(err(e)); } };
  return (
    <div data-testid="fb-page-videos" className="space-y-1.5">
      <p className="text-xs text-stone-600">Meta doesn't allow videos in Page comments through its API. Add each video yourself as a comment on the post, in this order, then tick it off.</p>
      {job.videos.map((v) => (
        <div key={v.order} className="flex flex-wrap items-center gap-2 text-xs">
          <Checkbox data-testid={`fb-page-video-${v.order}`} checked={v.comment_added} onCheckedChange={(x) => mark(v, !!x)} />
          <span className="font-semibold">#{v.order} {v.name}</span>
          <a href={`${API}/horses/${hid}/media-files/${v.key}/download`} className="inline-flex items-center gap-1 text-blue-800 underline"><Download className="h-3 w-3" />Download</a>
          {v.comment_added && <span className="text-emerald-700">Added{v.marked_by ? ` by ${v.marked_by}` : ""}</span>}
        </div>
      ))}
    </div>
  );
};

const Uncertain = ({ job, onDone }) => {
  const [url, setUrl] = useState("");
  const go = async (outcome) => { try { await api.post(`${B}/jobs/${job.id}/resolve`, { outcome, url: url || null }); onDone(); } catch (e) { toast.error(err(e)); } };
  return (
    <div data-testid="fb-page-uncertain" className="space-y-1.5 text-xs">
      <p className="text-amber-900">Meta didn't confirm the post. Check the Page before doing anything. It is never re-posted automatically.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="fb-page-uncertain-url" placeholder="Paste the Page post link if it's live" value={url} onChange={(e) => setUrl(e.target.value)} />
        <Button data-testid="fb-page-uncertain-live" size="sm" variant="outline" onClick={() => go("published")}>It's live</Button>
        <Button data-testid="fb-page-uncertain-not" size="sm" variant="outline" onClick={() => go("not_posted")}>Not posted</Button>
      </div>
    </div>
  );
};

export function FacebookPagePost({ hid, onChanged }) {
  const [d, setD] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conn, setConn] = useState(null);
  const load = () => api.get(`${B}/horses/${hid}`).then((r) => setD(r.data)).catch((e) => setD({ error: err(e) }));
  useEffect(() => { load(); }, [hid]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`${B}/connection`).then((r) => setConn(r.data)).catch(() => setConn({ ok: false, problem: "Couldn't check the connection." })); }, []);
  useEffect(() => {
    if (!d?.jobs?.some((j) => ["queued", "publishing"].includes(j.status))) return undefined;
    const t = setTimeout(load, 4000);  // keep refreshing until Meta answers
    return () => clearTimeout(t);
  }, [d]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="text-sm text-stone-500">Loading Facebook Page…</p>;
  if (d.error) return <p className="text-sm text-red-700">{d.error}</p>;
  const job = d.jobs.find((j) => !j.hidden);
  const test = d.jobs.find((j) => j.hidden);
  const posted = d.record.status === "published";
  const post = async (hidden = false) => {
    setBusy(true);
    try { await api.post(`${B}/horses/${hid}/publish`, { approve: true, hidden }); toast.success(hidden ? "Creating a hidden test post…" : "Posting to the Facebook Page…"); setTimeout(() => { load(); onChanged?.(); }, 5000); } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  return (
    <section data-testid="workspace-fb-page" className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-sm flex items-center gap-1.5"><Briefcase className="h-4 w-4" />Facebook Business Page</h3>
        <span data-testid="fb-page-status" className={`text-xs font-semibold border rounded-full px-2 py-0.5 ${posted ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-700 border-stone-200"}`}>
          {posted ? "Posted" : job ? LABEL[job.status] : d.connected && d.package.ready ? "Ready to Post" : "Not posted"}
        </span>
      </div>
      {!d.connected && <p data-testid="fb-page-not-connected" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">The Facebook Page isn't connected yet (Meta Page ID and Page access token needed), so posting is off. You can still preview.</p>}
      {d.connected && <Connection c={conn} />}
      {test?.status === "test_posted" && <TestPost job={test} onDone={load} />}
      {test && ["failed", "uncertain"].includes(test.status) && <p data-testid="fb-page-test-error" className="text-xs text-red-700">Hidden test: {test.events.at(-1)?.detail}</p>}
      {posted && d.record.url && (
        <a data-testid="fb-page-open" href={d.record.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1877F2] hover:underline">Open Facebook Page Post <ExternalLink className="h-3 w-3" /></a>
      )}
      {job?.status === "failed" && <p data-testid="fb-page-error" className="text-xs text-red-700">{job.events.at(-1)?.detail}</p>}
      {job?.status === "uncertain" && <Uncertain job={job} onDone={load} />}
      {job?.status === "published" && <Videos job={job} hid={hid} onDone={load} />}
      {!posted && !["queued", "publishing", "scheduled", "uncertain"].includes(job?.status) && (
        <>
          {d.package.problems.length > 0 && <ul data-testid="fb-page-problems" className="text-xs text-red-700 list-disc pl-4">{d.package.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
          <p className="text-xs text-stone-500">{d.package.photos_list.length} photo(s) in one post, in Ad Builder order{d.package.videos_list.length ? `; ${d.package.videos_list.length} video(s) added afterwards as comments by you` : ""}. Text: the FB Page & Website ad.</p>
          <Preview pkg={d.package} hid={hid} />
          <label className="flex items-start gap-2 text-xs text-stone-700">
            <Checkbox data-testid="fb-page-approve" checked={ok} onCheckedChange={(x) => setOk(!!x)} />
            <span>I approve posting this to {conn?.ok ? `${conn.page_name} (Page ID ${conn.page_id})` : d.page_name || "our Facebook Business Page"}.</span>
          </label>
          <Button data-testid="fb-page-post" disabled={!ok || busy || !conn?.ok || !d.package.ready} onClick={() => post(false)} className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Post to Facebook Page
          </Button>
          <Button data-testid="fb-page-test" variant="outline" disabled={!ok || busy || !conn?.ok || !d.package.ready || ["test_posted", "queued", "publishing"].includes(test?.status)} onClick={() => post(true)} className="w-full">
            Hidden test post (not public)
          </Button>
        </>
      )}
      {d.jobs.length > 0 && (
        <details data-testid="fb-page-history" className="text-xs">
          <summary className="cursor-pointer text-stone-600">History</summary>
          <ol className="mt-1 space-y-0.5">{d.jobs.flatMap((j) => j.events.map((e, i) => <li key={`${j.id}-${i}`}>{fmtMT(e.at)} · {LABEL[e.status] || e.status} · {e.detail} · {e.by}</li>))}</ol>
        </details>
      )}
    </section>
  );
}
